import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ByocService } from '../../../../service/byoc.service';
import { ByocCourseService } from '../../../service/course.service';

import { Mission } from '@app/core/model/library.model';

import { Pagination } from '@app/core/model/http-pagination.model';
import { LazyLoad } from '@app/core/model/pagination.model';
import { UserService } from '@app/user';
import { PerfectScrollbarDirective } from '@app/shared/perfect-scrollbar/perfect-scrollbar.directive';
import { control } from '../../../../utils';

@Component({
    selector: 'app-byoc-course-step-mission-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ByocCourseStepMissionListComponent implements OnInit, OnDestroy {
    @ViewChild('ps', { read: PerfectScrollbarDirective, static: true })
    psRef?: PerfectScrollbarDirective;

    @Input()
    searchFormControl!: UntypedFormControl;

    @Input()
    originControl!: UntypedFormControl;

    @Input()
    onHold = false;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly userService = inject(UserService);
    private readonly byocService = inject(ByocService);
    private readonly byocCourseService = inject(ByocCourseService);

    /**
     * Search Delay
     */
    delay: any;

    /**
     * Missions
     */
    missions: Mission[] = [];

    /**
     * missions added in popup
     */
    newlyCreatedMissions: Mission[] = [];

    /**
     * newly created missions that are not yet openable
     */
    inactiveMissions: string[] = [];

    /**
     * Is Searching flag
     */
    searching = false;

    /**
     * Subscriptions
     */
    private subscriptions: Subscription = new Subscription();

    /**
     * Search Subscription
     */
    private subscriptionSearch: Subscription = new Subscription();

    /**
     * lazyLoad
     */
    private lazyLoad: LazyLoad = {
        offset: {
            prefixPosition: 0,
            suffixPosition: 0,
        },
        totalItems: 0,
        limit: 50,
    };

    ngOnInit() {
        this.search();
        this.subscriptions.add(
            this.searchFormControl.valueChanges.subscribe(() => {
                this.newlyCreatedMissions.splice(0);
                this.search();
            }),
        );
        this.subscriptions.add(
            this.originControl.valueChanges.subscribe(() => {
                this.inactiveMissions.length = 0;
                this.newlyCreatedMissions.length = 0;
                this.search();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }
        if (this.delay) {
            clearTimeout(this.delay);
        }
    }

    /**
     * Course ID Control
     */
    get missionIdControl() {
        return control(this.byocCourseService.form.get('missionId'));
    }

    get scrollbarYEnabled(): boolean {
        return this.psRef?.ps?.scrollbarYActive ?? false;
    }

    get userName() {
        return this.userService.user.firstname + ' ' + this.userService.user.surname;
    }

    /**
     * Y Reached end event
     */
    psYReachEnd() {
        if (this.onHold) {
            return;
        }
        this.append();
    }

    /**
     * No result found
     */
    get noResults(): boolean {
        return !this.searching && this.lazyLoad.totalItems === 0;
    }

    /**
     * Search
     */
    search(): void {
        this.psRef?.scrollToTop();

        // clear value
        if (this.missionIdControl.value !== '') {
            this.missionIdControl.patchValue('');
        }

        // unsubscribe from active
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }

        // clear timtout from active
        if (this.delay) {
            clearTimeout(this.delay);
        }

        // reset lazyLoad
        this.resetLazyLoad();

        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.delay = setTimeout(() => {
            this.subscriptionSearch = this.byocService
                .searchMission(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
                .subscribe((data: { missions: Mission[]; pagination: Pagination }) => {
                    this.missions = data.missions.filter((mission) => !this.inactiveMissions.includes(mission.id));
                    this.lazyLoad.totalItems = data.pagination.totalItems;
                    this.lazyLoad.offset.suffixPosition += data.missions.length;
                    this.searching = false;
                    this.changeDetectorRef.detectChanges();
                    this.psRef?.ps.update();
                    // need to refresh
                    setTimeout(() => {
                        this.changeDetectorRef.detectChanges();
                    }, 0);
                });
        }, 250);
    }

    isMissionInactive(mission: Mission) {
        return typeof mission.preview !== 'undefined' && !mission.preview;
    }

    /**
     * Append Search
     */
    private append(): void {
        // don't run when already saerching
        if (this.searching) {
            return;
        }

        // don't run when all loaded
        if (this.missions.length + this.newlyCreatedMissions.length >= this.lazyLoad.totalItems) {
            return;
        }

        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.subscriptionSearch = this.byocService
            .searchMission(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
            .subscribe((data: { missions: Mission[]; pagination: Pagination }) => {
                this.lazyLoad.totalItems = data.pagination.totalItems;
                this.lazyLoad.offset.suffixPosition += data.missions.length;
                this.missions = this.missions.concat(data.missions).filter((mission) => !this.inactiveMissions.includes(mission.id));
                this.searching = false;
                this.changeDetectorRef.detectChanges();
                this.psRef?.ps.update();
                // need to refresh
                setTimeout(() => {
                    this.changeDetectorRef.detectChanges();
                }, 0);
            });
    }

    private resetLazyLoad(): void {
        this.lazyLoad = {
            offset: {
                prefixPosition: 0,
                suffixPosition: 0,
            },
            totalItems: 0,
            limit: 50,
        };
    }
}
